class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionHtml = parsedState.sections && parsedState.sections[section.id];
      if (!sectionHtml) return; // skip sections the server didn't return

      // For the cart icon: only swap the count badge, leave the rest of the
      // link (custom SVG, classes) untouched. Replacing the full innerHTML
      // would inject the default Dawn cart SVG and resize/reskin the icon.
      if (section.id === 'cart-icon-bubble') {
        this.updateCartIconBadge(sectionHtml);
        return;
      }

      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement) return;
      const innerHtml = this.getSectionInnerHTML(sectionHtml, section.selector);
      if (innerHtml !== null) sectionElement.innerHTML = innerHtml;
    });

    // Sync is-empty state with the freshly rendered content, since the
    // server-rendered HTML may have been authored when the cart was empty.
    const itemCount = typeof parsedState.item_count === 'number'
      ? parsedState.item_count
      : (this.querySelectorAll('.cart-item').length || 0);
    this.classList.toggle('is-empty', itemCount === 0);
    const drawerItems = this.querySelector('cart-drawer-items');
    if (drawerItems) drawerItems.classList.toggle('is-empty', itemCount === 0);

    requestAnimationFrame(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay && !overlay._aquaBound) {
        overlay.addEventListener('click', this.close.bind(this));
        overlay._aquaBound = true; // avoid stacking duplicate listeners on every render
      }
      this.open();
    });
  }

  updateCartIconBadge(sectionHtml) {
    const cartIcon = document.getElementById('cart-icon-bubble');
    if (!cartIcon) return;

    // Drop any existing badge (default Dawn or our custom one)
    cartIcon
      .querySelectorAll('.cart-count-bubble, .custom-header__cart-count')
      .forEach((el) => el.remove());

    if (!sectionHtml) return;

    // Pull just the new badge out of the rendered section response
    const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const newBadge = parsed.querySelector('.cart-count-bubble');
    if (newBadge) {
      cartIcon.appendChild(newBadge);
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return null;
    const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return parsed ? parsed.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
